// useFetchHolidays.js
import { useEffect, useState } from "react";
import axios from 'axios';
import dayjs from "dayjs";
import { API_AmericanHolidays, API_CanadianHolidays } from "../Assets/ApiUrlContainer";

const useFetchHolidays = (location) => {
  const [holidays, setHolidays] = useState([]);
  const [country, setCountry] = useState(null);
  const [province, setProvince] = useState(null);

  useEffect(() => {
    if (!location || typeof location !== "string") return;

    const lowerLocation = location.toLowerCase();

    const canadianRegions = {
      'alberta': 'AB', 'ab': 'AB',
      'british columbia': 'BC', 'bc': 'BC',
      'manitoba': 'MB', 'mb': 'MB',
      'new brunswick': 'NB', 'nb': 'NB',
      'newfoundland and labrador': 'NL', 'nl': 'NL',
      'nova scotia': 'NS', 'ns': 'NS',
      'ontario': 'ON', 'on': 'ON',
      'prince edward island': 'PE', 'pei': 'PE', 'pe': 'PE',
      'quebec': 'QC', 'qc': 'QC',
      'saskatchewan': 'SK', 'sk': 'SK',
      'northwest territories': 'NT', 'nt': 'NT',
      'nunavut': 'NU', 'nu': 'NU',
      'yukon': 'YT', 'yt': 'YT',
      'vancouver': 'BC', 'burnaby': 'BC', 'surrey': 'BC', 'richmond': 'BC',
      'new westminster': 'BC', 'coquitlam': 'BC',
      'hamilton': 'ON', 'toronto': 'ON', 'halifax': 'NS', 'comox': 'BC',
      'north vancouver': 'BC'
    };

    const usStates = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    for (const keyword in canadianRegions) {
      if (lowerLocation.includes(keyword)) {
        setCountry("CA");
        setProvince(canadianRegions[keyword]);
        return;
      }
    }

    for (const state of usStates) {
      const regex = new RegExp(`\\b${state}\\b`, "i");
      if (regex.test(location)) {
        setCountry("US");
        setProvince(state);
        return;
      }
    }
  }, [location]);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const today = dayjs();
        const nextYear = today.add(1, "year");
        const currentYear = today.year();
        const nextYearNumber = nextYear.year();

        let allHolidays = [];

        if (country === "CA" && province) {
          const resCurrent = await axios.get(`${API_CanadianHolidays}?year=${currentYear}`);
          const resNext = await axios.get(`${API_CanadianHolidays}?year=${nextYearNumber}`);
          const filter = (res) =>
            res.data.holidays.filter(h => h.provinces.some(p => p.id === province))
              .map(h => ({ date: h.date, name: h.nameEn }));

          allHolidays = [...filter(resCurrent), ...filter(resNext)];
        }

        if (country === "US") {
          const fetchUSHolidays = async (year) => {
            const res = await axios.get(`${API_AmericanHolidays}/${year}/US`);
            return res.data.map(h => ({ date: h.date, name: h.localName }));
          };
          allHolidays = [
            ...await fetchUSHolidays(currentYear),
            ...await fetchUSHolidays(nextYearNumber)
          ];
        }

        const filtered = allHolidays.filter(h => {
          const holidayDate = dayjs(h.date);
          return holidayDate.isSameOrAfter(today) && holidayDate.isBefore(nextYear);
        });

        setHolidays(filtered);
      } catch (err) {
        console.error("Failed to fetch holidays:", err);
        setHolidays([]);
      }
    };

    if (country) {
      fetchHolidays();
    }
  }, [country, province]);

  return { holidays, country, province };
};

export default useFetchHolidays;